import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import L from 'leaflet';
import { DateTime } from 'luxon';
import type { Member, MemberLocation } from '@pacer/shared';
import { createPortal } from 'react-dom';
import * as api from '../api';
import { Icon } from './Icon';
import { queryKeys } from '../state/queries';
import { useTheme } from '../state/useTheme';

// Free tiles, no API key — CARTO's themed basemaps so the map matches light/dark.
const TILES = {
  light: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
  dark: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
};
const ATTRIBUTION = '© OpenStreetMap · © CARTO';

/** A member marker: their photo or colour monogram in a pin, as Leaflet HTML. */
function markerFor(member: Member): L.DivIcon {
  const inner = member.avatarUrl
    ? `<img src="${member.avatarUrl}" alt="" style="width:100%;height:100%;object-fit:cover;border-radius:9999px" />`
    : `<span style="font:600 13px 'IBM Plex Mono',monospace;color:${member.color}">${member.name.slice(0, 1).toUpperCase()}</span>`;
  return L.divIcon({
    className: '',
    html: `<div style="width:36px;height:36px;border-radius:9999px;display:flex;align-items:center;justify-content:center;
      background:color-mix(in srgb, ${member.color} 22%, #111);border:2px solid ${member.color};
      box-shadow:0 2px 8px rgba(0,0,0,.5);overflow:hidden">${inner}</div>`,
    iconSize: [36, 36],
    iconAnchor: [18, 18],
    popupAnchor: [0, -20],
  });
}

/**
 * A map of where the family is — pins from each member's last known location (a manual
 * check-in or a phone automation). Rendered inside Pacer rather than kicking out to an
 * external maps app.
 */
export function FamilyMap({ focusMemberId, onClose }: { focusMemberId?: string; onClose: () => void }) {
  const { resolved } = useTheme();
  const el = useRef<HTMLDivElement>(null);
  const map = useRef<L.Map | null>(null);
  const tiles = useRef<L.TileLayer | null>(null);

  const { data: locations = [] } = useQuery({ queryKey: queryKeys.locations, queryFn: api.getLocations });
  const { data: members = [] } = useQuery({ queryKey: queryKeys.members, queryFn: api.getMembers });

  const pins = locations
    .map((loc) => ({ loc, member: members.find((m) => m.id === loc.memberId) }))
    .filter((x): x is { loc: MemberLocation; member: Member } => Boolean(x.member));

  // Create the map once.
  useEffect(() => {
    if (!el.current || map.current) return;
    const m = L.map(el.current, { zoomControl: true, attributionControl: true }).setView([20, 0], 2);
    tiles.current = L.tileLayer(TILES[resolved], { attribution: ATTRIBUTION, maxZoom: 19 }).addTo(m);
    map.current = m;
    // The container sizes after mount inside the modal, so tell Leaflet to remeasure.
    setTimeout(() => m.invalidateSize(), 60);
    return () => {
      m.remove();
      map.current = null;
    };
  }, [resolved]);

  // Swap tiles on theme change.
  useEffect(() => {
    if (!map.current || !tiles.current) return;
    tiles.current.setUrl(TILES[resolved]);
  }, [resolved]);

  // (Re)draw markers when locations change.
  useEffect(() => {
    const m = map.current;
    if (!m) return;
    const layer = L.layerGroup().addTo(m);
    for (const { loc, member } of pins) {
      const marker = L.marker([loc.lat, loc.lon], { icon: markerFor(member) }).addTo(layer);
      const ago = DateTime.fromISO(loc.updatedAt).toRelative({ style: 'short' });
      marker.bindPopup(
        `<div style="font-family:'IBM Plex Sans',sans-serif;min-width:120px">
           <strong>${member.name}</strong>${loc.label ? ` · ${loc.label}` : ''}<br/>
           <span style="font-size:11px;color:#888">updated ${ago}</span><br/>
           <a href="https://maps.google.com/?q=${loc.lat},${loc.lon}" target="_blank" rel="noreferrer"
              style="font-size:12px">Open in Maps</a>
         </div>`,
      );
    }

    if (pins.length === 1) {
      m.setView([pins[0]!.loc.lat, pins[0]!.loc.lon], 14);
    } else if (pins.length > 1) {
      m.fitBounds(L.latLngBounds(pins.map((x) => [x.loc.lat, x.loc.lon])).pad(0.25));
    }

    const focus = focusMemberId ? pins.find((x) => x.member.id === focusMemberId) : undefined;
    if (focus) m.setView([focus.loc.lat, focus.loc.lon], 14);

    return () => {
      layer.remove();
    };
  }, [pins, focusMemberId]);

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center sm:p-4" style={{ background: 'rgba(0,0,0,0.6)' }}>
      <div className="flex h-full w-full flex-col overflow-hidden border border-border bg-surface-high shadow-[0_20px_60px_rgba(0,0,0,0.5)] sm:h-[85vh] sm:max-w-3xl sm:rounded-lg">
        <header className="flex shrink-0 items-center justify-between border-b border-border-subtle px-5 py-3">
          <div>
            <span className="eyebrow">Where everyone is</span>
            <h2 className="font-display text-[18px] text-primary">Family map</h2>
          </div>
          <button
            onClick={onClose}
            className="touch press -mr-1.5 rounded-lg px-3 text-2xl leading-none text-text-dim hover:text-text"
            aria-label="Close"
          >
            ×
          </button>
        </header>
        <div className="relative min-h-0 flex-1">
          <div ref={el} className="absolute inset-0" style={{ background: 'var(--color-bg)' }} />
          {pins.length === 0 && (
            <div className="pointer-events-none absolute inset-0 z-[500] flex flex-col items-center justify-center gap-2 text-center">
              <Icon name="map" size={28} className="text-muted" />
              <p className="text-[14px] text-secondary">No locations shared yet</p>
              <p className="max-w-xs font-mono text-[12px] text-muted">
                Tap “Share my location” on a status, or set up the arrive/leave automation in Settings.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
